import { Outlet } from "react-router-dom";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  compareMeetingSessionRoles,
  isMeetingSessionRoleGreaterThanOrEqual,
  isMeetingSessionRoleLessThan,
  isSpectator,
  MeetingSessionOvenMediaReceiver,
  useMeetingAudioMixer,
  useMeetingSelector
} from "@kalyzee/kast-web-sdk";
import {
  audioContextIsReadyToUse,
  AudioMixerOptions,
  awaitWithMinimumDelay,
  MediaCaptureRef,
  useOuterClick,
  useRefAutoUpdate,
  useStateWithRef
} from "@kalyzee/kast-web-sdk/web-components";
import { MeetingRoomStatus, MeetingSessionAddMedia, MeetingSessionHighlightingOnResponseActionHandler, MeetingSessionHighlightingOnStartActionHandler, MeetingSessionHighlightingState, MeetingSessionRole, MeetingState, MeetingUpdateByIdResult, SocketActionHandler, SurveyStatus } from "@kalyzee/kast-web-sdk/websocket";
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError, toastInfo, toastSuccess } from "../../../../helpers/toast";
import { MeetingSourceExtended, useMeetingConfigurator, useMeetingContext, useMeetingDataOnChange, useMeetingSources } from "../../../../hooks/meeting";

import { RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { DEFAULT_AUDIO_MATCH } from "../../../../helpers/defaultMatchers";
import { isExpertVideoSession } from "../../../../helpers/meeting";
import { matchEngine } from "../../../../helpers/matchEngine";

import PauseView from "../../../../components/utils/PauseView";
import { useSettings } from "../../../../hooks/settings";

import { BottomPanelWebrtcSessionStats } from "../../../../components/debug/BottonPanelWebrtcSessionStats";
import { DevSoundIndicatorsBanner } from "../../../../components/utils/DevSoundIndicatorBanner";
import OverlayInformationMessage from "../../../../components/utils/OverlayInformationMessage";

import { BoxOverlay, BoxOverlayProps, CircleIcon, OverlayContainer, PrimaryButton, ReactIcons, RevealContainer, SecondarySideBar } from "@kalyzee/kalyzee-ui";
import { MeetingContainer, MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { MediaSourceWorkBox } from "../../../../components/workBoxes/MediaSourceWorkBox";
import { MediaSourceViewer } from "../../../../components/common/MediaSourceViewer";
import { BoardInputsWorkBox } from "../../../../components/workBoxes/BoardInputsWorkBox";
import { ChatWorkBox } from "../../../../components/workBoxes/ChatWorkBox";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { getAudioContextForAudioMixer, getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { addPopup, removePopup, removePopupByGroup } from "../../../../helpers/popup";
import { isExpert } from "../../../../helpers/roles";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";

import clsx from "clsx";
import Chat, { DashboardChatRef } from "../../../../components/chat/Chat";
import { MainClockElapsedRenderer } from "../../../../components/renderers/mainClock";
import { AppSoundSlider } from "../../../../components/utils/AppSoundSlider";
import { LoadingView } from "../../../../components/utils/LoadingView";
import { HighlightedUserListWorkBox } from "../../../../components/workBoxes/list/HighlightedUserListWorkBox";
import { SurveyWorkBox } from "../../../../components/workBoxes/survey/SurveyWorkbox";
import { useAppStoreContext, useAppStoreDispatch } from "../../../../contexts/appStore/context";
import { useMeetingUtilsContext } from "../../../../contexts/meetingUtils/context";
import { MeetingUtilsProvider } from "../../../../contexts/meetingUtils/provider";
import { useBoardMedia } from "../../../../hooks/board";
import { useMeetingMasterMessagingBridge } from "../../../../hooks/secondaryScreens/messagingBridge";

import { LayoutType } from "../../../../interfaces/ui";
import styles from "./meeting.module.css";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { HighlightButton } from "../../../../components/highlight/button";
import { useHighlightNotifications, useHighlightSessions } from "../../../../hooks/highlight";
import { useFeatures } from "../../../../hooks/features";
import { SideBar } from "../../../../components/common/SideBar";
import { CurrentWorkBox } from "../../../../components/common/CurrentWorkBox";
import { HighlightPopup } from "../../../../components/common/HighlightPopup";
import { SurveyBar } from "../../../../components/common/SurveyBar";
import { MeetingContainerProvider } from "../../../../contexts/meetingContainer/provider";
import { BottomBar } from "../../../../components/common/BottomBar";
import { SourcePickerAlwaysOpen } from "../../../../components/common/SourcePickerAlwaysOpen";
import { useDelayedState } from "../../../../hooks/states";

export const MeetingInitPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const sources = useMeetingSources();

  const dispatch = useAppStoreDispatch();

  const meetingCtx = useMeetingContext();

  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);

  const meetingsAPI = meetingCtx?.api;
  const meetingsAPIRef = useRefAutoUpdate(meetingsAPI);

  const container = useMemo(() => {
    if (!meetingsAPI) return undefined;

    const containers = meetingsAPI.getContainers();
    const selectedId = pageContextRef.current?.params?.id;

    if (selectedId) {
      return containers.find((c) => c.getMeetingId() === selectedId);
    }

    return containers[0];
  }, [meetingsAPI]);

  // Avoids flickering
  const [showMeeting] = useDelayedState(container !== undefined, 1000);

  const joinContainer = async (container: MeetingContainer) => {
    if (container.getStatus() !== "disconnected") return;

    const containerAPI = container.getAPI();
    
    if (audioEnabled) await containerAPI.unmute({ direction: "outgoing", group: "cameras" });
    else await containerAPI.mute({ direction: "outgoing", group: "cameras" }); 
    if (videoEnabled) await containerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
    else await containerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
    dispatch("setAudioStreamEnabled", containerAPI.emissionManager.audioIsEnabled({ group: "cameras" }));
    dispatch("setVideoStreamEnabled", containerAPI.emissionManager.videoIsEnabled({ group: "cameras" }));
   
    const medias: MeetingSessionAddMedia<any, any, any>[] = (sources as MeetingSourceExtended[]).map((s) => {
      const type = s.extra.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      return {
        video: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.videoIsEnabled({ group }),
        },
        audio: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.audioIsEnabled({ group }),
        },
        name: s.name,
        deviceId: s.id,
        extra: {
          type,
          label: s.name,
          controls: s.controls?.type,
        },
      };
    });

    const result = await containerAPI.join({
      medias,
    });
   
    if (result.error) {
      console.error(`[ERROR] join: `, result.error);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      container.destroy();
      return;
    }
    const session = result.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      container.destroy();
      return;
    }
  };

  const joinTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const createAndJoinContainer = async (id: string) => {
    const api = meetingsAPIRef.current;

    if (!api) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      return;
    }

    // Fixing a small issue
    //
    // during dev hot-reload :
    // - the meeting is left ([] cleanup on unmount)
    // - here we fetch a container that is "disconnected" but just not yet removed from the list
    //   (the list removal is handled in the react workflow)
    // - no new container is created and we're left hanging with a disconnected container
    //
    // -> fix, with setTimeout, we postpone the getOrCreateContainer after the disconnected container
    // is removed from the list by react, and now we can create a new one
    joinTimeoutRef.current = setTimeout(() => {
      const container = api.getOrCreateContainer(id);
      joinContainer(container);
    }, 0);
  };

  const leave = async () => {
    const promises: Promise<any>[] = [];
    meetingsAPIRef.current?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  useMeetingConfigurator(container?.getAPI(), {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.name === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.screensharing ?? DEFAULT_MAX_BITRATES.student.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
                },
              },
            };
          }
          const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.webcam ?? DEFAULT_MAX_BITRATES.student.webcam;
          return {
            rtc: {
              streamParams: {
                preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
              },
            },
          };
        },
      },
      reception: {
        options: () => ({
          video: { mode: "subscription", delayToDisableStream: 5000 },
          audio: true,
        }),
      },
    },
  });

  useEffect(() => {
    const id = pageContextRef.current?.params?.id ?? "";
    audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer()).then((ready) => {
      if (!ready) {
        const queries: any = {};
        if (id) queries["meeting"] = id;
        pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries });
      } else {
        createAndJoinContainer(id);
      }
    });
    return () => {
      if (joinTimeoutRef.current) clearTimeout(joinTimeoutRef.current);
      leave();
    };
  }, []);

  if (!container || !showMeeting) {
    return <LoadingView fullscreen message="Connexion au meeting ..." />;
  }

  return (
    <MeetingContainerProvider container={container}>
      <MeetingUtilsProvider>
        <MeetingPage />
      </MeetingUtilsProvider>
    </MeetingContainerProvider>
  );
}

export const MeetingPage = () => {
  const [settings] = useSettings();

  const features = useFeatures();

  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const sdkCtx = useSdkContext();

  const selfUser = useAppStoreSelector(state => state.user);
  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const outputAudioDeviceInfo = useAppStoreSelector(state => state.outputAudioDeviceInfo);
  const meetingOptions = useAppStoreSelector(state => state.meetingOptions);
  const workBoxOpen = useAppStoreSelector(state => state.workBoxOpen);

  const dispatch = useAppStoreDispatch();
  const appStoreCtx = useAppStoreContext();

  const { containerAPI, populated } = useMeetingUtilsContext();
  const containerAPIRef = useRefAutoUpdate(containerAPI);

  const [showEndMeetingConfirmationPopup, setShowEndMeetingConfirmationPopup] = useState(false);

  const autojoin = pageContextRef.current?.searchParams?.get("autojoin") === "true";

  const [meeting] = useMeetingSelector(containerAPI, "meeting");
  const [session] = useMeetingSelector(containerAPI, "session");
  const [sessions] = useMeetingSelector(containerAPI, "sessions")
  const [rooms] = useMeetingSelector(containerAPI, "rooms");
  const [emissionSessions] = useMeetingSelector(containerAPI, "emissionSessions");
  const [receptionSessions] = useMeetingSelector(containerAPI, "receptionSessions");
  const [chatrooms] = useMeetingSelector(containerAPI, "chatrooms");

  const mediaSelectorRef = useRef<MediaCaptureRef>();

  const [chatAdapters] = useMeetingSelector(containerAPI, "chatAdapters");
  const chatInfo = useMemo(() => {
    const room = chatrooms?.[0];
    if (!room) return undefined;
    const adapter = chatAdapters?.[room.id];
    return { room, adapter };
  }, [chatrooms, chatAdapters]);

  const [unreadMessagesNb, setUnreadMessagesNb] = useState(0);
  useEffect(() => {
    const handle = chatInfo?.adapter?.addEventListener("messages", (messages) => {
      setUnreadMessagesNb(messages.filter(m => !m.read).length);
    });
    return () => handle?.flush();
  }, [chatInfo?.adapter]);

  const isStoppingRef = useRef(false);

  const sources = useMeetingSources();

  const usableSources: MeetingSourceExtended[] = useMemo(() => {
    return isSpectator(session?.role) ? [] : ((sources ?? []) as any);
  }, [sources, session?.role]);

  const boardLocalOrRemoteMedia = useBoardMedia();

  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!meeting?.id) return { enabledMedia: [] };
    const studentMeetingOptions = meetingOptions[meeting.id];

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));
    
    return { enabledMedia };
  }, [meeting?.id, boardLocalOrRemoteMedia, meetingOptions]);

  // Handles all the notifications 
  // and the confirmation popup
  useHighlightNotifications();

  useMeetingMasterMessagingBridge();

  useMeetingDataOnChange(
    containerAPI,
    (state) => state?.session?.permissions?.audio ?? true,
    (value, previous) => {
      if (value) toastSuccess("Vous pouvez à nouveau activer votre micro.");
      else toastError("L'activation du micro est désormais bloquée.");
    }
  );

  useEffect(() => {
    const emissionManager = containerAPI?.emissionManager;
    if (emissionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (emissionManager.paused) emissionManager.restore();
      } else {
        if (!emissionManager.paused) emissionManager.pause();
      }
    }
    const receptionManager = containerAPI?.receptionManager;
    if (receptionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (receptionManager.paused) receptionManager.restore();
      } else {
        if (!receptionManager.paused) receptionManager.pause(); 
      }
    }
  }, [containerAPI, meeting?.state])

  ////////////////////// Work Boxes DATA //////////////////////

  const audioMatchRoleComparator = useMemo(() => ({ "session.role": (a: any, b: any) => -compareMeetingSessionRoles(a, b) }), []);

  const canHearSession = useCallback((_: MeetingContainerAPI, receiverSession: MeetingSessionOvenMediaReceiver) => {
    const freshSession = sessions?.find(s => s.id === receiverSession.data.session.id);
    const freshMedia = freshSession?.medias.find(m => m.id === receiverSession.data.mediaSession.id);
    if (!freshSession || !freshMedia) return false;
    const highlighted = populated.activity?.mode === "highlight"
      && populated.activity.confirmedSessions.some(cs => cs.session.id === freshSession.id);
    return matchEngine(
      { session: freshSession, media: freshMedia, currentSession: session, highlighted: !!highlighted },
      features.audioMatch || DEFAULT_AUDIO_MATCH,
      audioMatchRoleComparator,
    );
  }, [features.audioMatch, audioMatchRoleComparator, populated.activity, session, sessions]);

  const audioMixerOptions: AudioMixerOptions = useMemo(() => ({
    ...(settings.debug?.audio?.mixer ?? {}),
    // No forced equalizer for now 
    //
    // equalizer: {
    //   samplingInterval: 500, // (ms) 80 -> 500
    //   fftSize: 256, // (samples) 1024 -> 256
    //   freqRange: "voice",
    //   smoothingSamplingCount: 5, 
    //   target: 0.5,
    // },
    forceEnableDecoderHack: true,
    
  }), [settings.debug?.audio?.mixer]);

  const audioMixer = useMeetingAudioMixer(
    getAudioContextForAudioMixer(), 
    containerAPI, 
    true, 
    canHearSession, 
    audioMixerOptions
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const updateMedias = useCallback(async () => {
    /*
      Here we enable / disable the video / audio emission locally
      according to the backend media state
    */

    const meetingContainerAPI = containerAPIRef.current;
    if (!meetingContainerAPI) return;

    const manager = meetingContainerAPI.emissionManager;

    if (session) {
      session.medias?.forEach((m) => {
        if (!m.video.enabled && manager.videoIsEnabled({ mediaSessionId: m.id })) {
          manager.disableVideo({ mediaSessionId: m.id });
        } else if (m.video.enabled && !manager.videoIsEnabled({ mediaSessionId: m.id })) {
          manager.enableVideo({ mediaSessionId: m.id });
        }
        if (!m.audio.enabled && manager.audioIsEnabled({ mediaSessionId: m.id })) {
          manager.disableAudio({ mediaSessionId: m.id });
        } else if (m.audio.enabled && !manager.audioIsEnabled({ mediaSessionId: m.id })) {
          manager.enableAudio({ mediaSessionId: m.id });
        }
      });
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }

    dispatch("setAudioStreamEnabled", manager.audioIsEnabled({ group: "cameras", mode: "some" }));
    dispatch("setVideoStreamEnabled", manager.videoIsEnabled({ group: "cameras", mode: "some" }));
  }, [dispatch, session, meeting]);

  // ------------------ EXPERT UTILS ------------------ //

  const updateMeetingState = useCallback(async (state: MeetingState) => {
    pageContextRef.current?.setLoading(true);
    let result: MeetingUpdateByIdResult | undefined;
    try {
      result = await containerAPIRef.current?.updateMeetingState(state);
    } catch (err) {
    } finally {
      pageContextRef.current?.setLoading(false);
    }
    return result;
  }, []);

  const start = async (): Promise<boolean> => {
    isStoppingRef.current = true;
    pageContextRef.current?.setLoading(true);
    const result = await awaitWithMinimumDelay(updateMeetingState(MeetingState.RUNNING), 500);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.RUNNING) {
      return true;
    }
    isStoppingRef.current = false;
    toastError("Une erreur est apparue.");
    return false;
  };

  const onPause = useCallback(async () => {
    updateMeetingState(MeetingState.PAUSED);
  }, []);

  const onStop = useCallback(async () => {
    setShowEndMeetingConfirmationPopup(true);
  }, []);

  const onResume = useCallback(async () => {
    updateMeetingState(MeetingState.RUNNING);
  }, []);

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    // Not ready to use, we cannot decide if a redirection is needed
    if (!meeting) return;

    // I am in a pedagogy meeting
    if (meeting.specializedData?.isPedagogy) {
      if (pageContextRef.current?.page === PageMasterMode.MEETING_PEDAGOGY) {
        return;
      }
      // I keep the ID of my current meeting, this will be used as an origin meeting
      // => to know what meeting i'm coming from
      pageContextRef.current?.goTo(PageMasterMode.MEETING_PEDAGOGY, { id: pageContextRef.current?.params?.id });
      return;
    }

    // Nothing is highlighted, nothing is active, we can go back to the dashboard
    if (pageContextRef.current?.page !== PageMasterMode.MEETING_DASHBOARD) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: pageContextRef.current?.params?.id });
    }
  }, [pageContextRef]);

  useEffect(() => {
    if (!mediaSelectorRef.current) return;
    mediaSelectorRef.current.enableAudio(audioEnabled);
    mediaSelectorRef.current.enableVideo(videoEnabled);
  }, [audioEnabled, videoEnabled]);

  useEffect(() => {
    if (outputAudioDeviceInfo && audioMixer?.isRunning()) {
      audioMixer.setAudioOutputDevice(outputAudioDeviceInfo).catch(() => {
        console.error(`audio device (${JSON.stringify(outputAudioDeviceInfo)}) is invalid`);
      });
    }
  }, [audioMixer, outputAudioDeviceInfo]);

  useEffect(() => {
    if (!emissionSessions) return;
    emissionSessions.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      if (group && (!s.data.groups || s.data.groups[0] !== group || s.data.groups.length !== 1)) {
        s.data.groups = [group];
      }
    });
  }, [emissionSessions]);

  useEffect(() => {
    if (!receptionSessions) return;
    receptionSessions.forEach((s) => {
      const isExpertVideo = isExpertVideoSession(meeting, s);
      if (s.data.extra?.video !== isExpertVideo || s.data.extra?.audio !== true) {
        s.data.extra = {
          video: isExpertVideo,
          audio: true,
        };
      }
    });
  }, [receptionSessions, meeting]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    if (settings.debug?.audio?.mixer?.public) (window as any).audioMixer = audioMixer;
    if (settings.debug?.meetingContext?.public) (window as any).meetingContext = containerAPI;
  }, [settings, audioMixer, containerAPI]);

  // ------------------ EXPERT EFFECTS ------------------ //

  useEffect(() => {
    const state = meeting?.state;

    let currentPopup: React.ReactNode;
    const endMeetingPopup = () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }

      const stopped = state === MeetingState.STOPPED;

      let buttons: BoxOverlayProps["buttons"];
      
      if (stopped) {
        buttons = [
          {
            label: "Revenir à la liste des cours",
            onClick: () => {
              removePopup(currentPopup);
              removePopupByGroup("main");
              pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
            },
            validateAfter: autojoin ? 10 : undefined,
          }
        ];
        if (isExpert(session)) {
          buttons.push({
            label: "Relancer le cours",
            onClick: async () => {
              await start();
              removePopup(currentPopup);
            }
          });
        }
      } else {
        buttons = [
          {
            label: "Terminer le cours",
            onClick: async () => {
              await updateMeetingState(MeetingState.STOPPED);
            }
          },
          {
            label: "Poursuivre le cours",
            onClick: () => {
              setShowEndMeetingConfirmationPopup(false);
            }
          }
        ]
      }

      currentPopup = (
        <OverlayContainer
          visible
          style={{
            width: "100vw",
            height: "100vh",
          }}
          overlay={
            <BoxOverlay
              title={stopped ? "Le cours est terminé" : "Voulez-vous vraiment terminer le cours ?"}
              level={stopped ? "success" : "question"}
              buttons={buttons}
            />
          }
        />
      );
      
      addPopup(currentPopup, "meeting_state_stopped", "main");
    };

    if (state === MeetingState.STOPPED) {
      setShowEndMeetingConfirmationPopup(false);
    }

    if (state === MeetingState.IDLE) {
      start();
    } else if (state === MeetingState.STOPPED || showEndMeetingConfirmationPopup) {
      endMeetingPopup();
    }

    return () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state, showEndMeetingConfirmationPopup]);

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const workBoxMode = useAppStoreSelector(state => state.workBoxMode);

  const sourcePickerOpen = useAppStoreSelector(state => state.sourcePickerOpen);

  // When switching to mobile: reset workbox, source picker, and force horizontal layout
  useEffect(() => {
    if (isMobile) {
      dispatch("setWorkBoxOpen", false);
      dispatch("setSourcePickerOpen", false);
      const currentLayout = appStoreCtx.getState().layout;
      if (currentLayout === LayoutType.VerticalSquare) {
        dispatch("setLayout", LayoutType.HorizontalSquare);
      }
    }
  }, [isMobile]);

  const highlightRequestsNb = populated.highlighted.requesting.length;

  type MobileWorkBoxMode = "inputs" | "survey" | "highlight" | "chat";
  const mobileWorkBoxTabs = useMemo(() => {
    const iconStyle = { width: 18, height: 18 };
    const tabs: { mode: MobileWorkBoxMode; icon: React.ReactNode; notificationCount?: number }[] = [
      { mode: "inputs", icon: <ReactIcons.ComputerScreen style={iconStyle} /> },
    ];
    if (features.survey) tabs.push({ mode: "survey", icon: <ReactIcons.Stats style={iconStyle} /> });
    if (isExpert(session) && features.board) tabs.push({ mode: "highlight", icon: <ReactIcons.Hand2 style={iconStyle} />, notificationCount: highlightRequestsNb || undefined });
    tabs.push({ mode: "chat", icon: <ReactIcons.ChatBubble style={iconStyle} />, notificationCount: unreadMessagesNb || undefined });
    return tabs;
  }, [features.survey, features.board, session, highlightRequestsNb, unreadMessagesNb]);

  const handleMobileTabClick = useCallback((mode: MobileWorkBoxMode) => {
    dispatch("setWorkBoxMode", mode);
  }, [dispatch]);

  const closeWorkBoxOverlay = useCallback(() => {
    dispatch("setWorkBoxOpen", false);
  }, [dispatch]);

  const openSourcesOverlay = useCallback(() => {
    dispatch("setSourcePickerOpen", true);
  }, [dispatch]);

  const closeSourcesOverlay = useCallback(() => {
    dispatch("setSourcePickerOpen", false);
  }, [dispatch]);

  if (!meeting || !session || !rooms || !selfUser)  return <LoadingView fullscreen message="Chargement en cours" />;

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderDevSoundIndicator = () => (
    <div style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
      <DevSoundIndicatorsBanner
        meetingData={populated}
        canHearSession={(session) => canHearSession(containerAPI, session)}
      />
    </div>
  );

  const renderMobileWorkBoxOverlay = () => {
    if (!isMobile) return null;
    return (
      <OverlayContainer
        visible={workBoxOpen}
        onClose={closeWorkBoxOverlay}
        overlayStyle={{ padding: "1em", gap: "0.6em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <>
            <div className={styles.mobileWorkBoxTabs} onClick={(e) => e.stopPropagation()}>
              {mobileWorkBoxTabs.map((tab) => (
                <PrimaryButton
                  key={tab.mode}
                  className={clsx(styles.mobileWorkBoxTab, {
                    [styles.mobileWorkBoxTabActive]: workBoxMode === tab.mode,
                  })}
                  onClick={() => handleMobileTabClick(tab.mode)}
                >
                  {tab.notificationCount !== undefined && tab.notificationCount > 0 && workBoxMode !== tab.mode && (
                    <div className={styles.mobileWorkBoxTabBadge}>{tab.notificationCount}</div>
                  )}
                  {tab.icon}
                </PrimaryButton>
              ))}
            </div>
            <div
              className={styles.mobileWorkBoxOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <CurrentWorkBox chatInfo={chatInfo} onClose={closeWorkBoxOverlay} />
            </div>
          </>
        }
      />
    );
  };

  const renderMobileSourcesOverlay = () => {
    if (!isMobile) return null;
    return (
      <OverlayContainer
        visible={!!sourcePickerOpen}
        unmountWhenInvisible={false}
        onClose={closeSourcesOverlay}
        overlayStyle={{ padding: "1em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <div
            className={styles.mobileWorkBoxOverlay}
            onClick={(e) => e.stopPropagation()}
          >
            <MediaSourceWorkBox onClose={closeSourcesOverlay}>
              <MediaSourcePicker
                meetingAPI={containerAPI}
                sources={usableSources}
                display
                style={{ padding: 0, width: "100%", height: "100%" }}
              />
            </MediaSourceWorkBox>
          </div>
        }
      />
    );
  };

  const renderUI = (children: React.ReactNode) => {
    if (isMobile) {
      return (
        <div className={clsx(styles.uiContainer, styles.uiContainerMobile)}>
          <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}>
            <SurveyBar />
            <div style={{
              height: "100%",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              padding: "0.5em",
              gap: "0.5em",
            }}>
              <div style={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                position: "relative",
              }}>
                {children}
                {renderDevSoundIndicator()}
              </div>
              <BottomBar
                onUpdateMeetingState={updateMeetingState}
                onPause={onPause}
                onStop={onStop}
                sources={usableSources}
                showLayoutSelector={enabledMedia.length > 1}
                onOpenSources={openSourcesOverlay}
                sourcesActive={sourcePickerOpen}
                notificationCount={(unreadMessagesNb || 0) + (populated.highlighted.requesting.length || 0) || undefined}
              />
            </div>
          </div>
          {renderMobileWorkBoxOverlay()}
          {renderMobileSourcesOverlay()}
        </div>
      );
    }

    return (
      <div className={clsx({
        [styles.uiContainer]: true,
        [styles.boxOpen]: workBoxOpen,
      })}>
        <SideBar
          chatAdapter={chatInfo?.adapter}
          highlightRequestsNb={populated.highlighted.requesting.length}
        />
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}>
          <SurveyBar />
          <div style={{
            height: "100%",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: "1em 1em 1em 0em",
            gap: "1em",
          }}>
            <div style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
            }}>
              <div className={styles.workContainer}>
                <RevealContainer
                  open={workBoxOpen}
                  gap="1em"
                  duration={500}
                  easing="cubic-bezier(.4, 0, .2, 1)"
                  className={styles.wbRevealContainer}
                >
                  <CurrentWorkBox
                    chatInfo={chatInfo}
                  />
                </RevealContainer>
                <div className={styles.childrenContainer}>
                  {children}
                  <SourcePickerAlwaysOpen sources={usableSources} />
                  {renderDevSoundIndicator()}
                </div>
              </div>
            </div>
            <BottomBar
              onUpdateMeetingState={updateMeetingState}
              onPause={onPause}
              onStop={onStop}
              sources={usableSources}
              showLayoutSelector={enabledMedia.length > 1}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderDebugOutgoingStream = () => {
    if (!settings?.debug?.displayOutgoingStreamStats && !settings?.debug?.displaySessionIds) return null;
    return (
      <BottomPanelWebrtcSessionStats
        incomingSessions={settings?.debug?.displayIncomingStreamStats ? receptionSessions : undefined}
        outgoingSessions={settings?.debug?.displayOutgoingStreamStats ? emissionSessions : undefined}
        sessions={(emissionSessions ?? []).map((s, i) => ({
          id: s.getId(),
          name: `${s.data?.mediaSession?.name ?? `ouput n°${i}`}`,
          session: settings?.debug?.displayOutgoingStreamStats ? s : undefined,
          meeting: settings?.debug?.displaySessionIds ? { session: session, mediaSession: s.data?.mediaSession } : undefined,
        }))}
      />
    );
  };

  return (
    <MasterUI>
      <PauseView
        onResume={onResume}
        enabled={isExpert(session)}
        meetingPaused={meeting?.state === MeetingState.PAUSED}
        meetingPausedAt={meeting?.pausedAt}
      >
        <div className={styles.mainContainer}>
          {isSpectator(session) ? <div className={styles.spectatorBar}>Spectateur</div> : null}
          {renderUI(<Outlet />)}
          {renderDebugOutgoingStream()}
          <HighlightPopup />
        </div>
      </PauseView>
    </MasterUI>
  );
};
